import { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { HighlightableTextWithCitation } from './highlightable-text/HighlightableText';
import HighlightedTextProps from './HighlightedText.props';
import './styles.css';
import { TokenReference } from '../../services/reference-generator/types';
import { Logger } from '../../utilities';

export interface HTMLPart {
  text: string;
  tokenReference?: TokenReference;
}

const HighlightedText = ({
  sourceText,
  convertTextToHtml = true,
  onCitationPushed,
  citations = [],
}: HighlightedTextProps) => {
  const [error, setError] = useState<string>('');
  const [htmlText, setHtmlText] = useState<string>('');
  const [parts, setParts] = useState<HTMLPart[]>([]);

  const generateHtml = useCallback(async () => {
    // must create new worker on each execution
    const worker = new Worker(new URL('../../workers/makrdown-to-html.worker', import.meta.url));

    try {
      const promise = new Promise<string>((res, rej) => {
        worker.onmessage = (event: MessageEvent<string>) => {
          res(event.data);
        };
        worker.onerror = error => {
          rej(error);
        };
        worker.postMessage({ sourceText });
      });
      const response = await promise;
      setHtmlText(response);
    } catch (error: any) {
      Logger.error('Error generating HTML from worker', error);
      setError('An error occurred while generating the HTML');
    } finally {
      worker.terminate();
    }
  }, [sourceText]);

  useEffect(() => {
    // Reset state when sourceText changes
    setHtmlText('');
    setParts([]);
    setError('');

    if (!convertTextToHtml) {
      setHtmlText(sourceText);
      return;
    }

    if (!sourceText) return;

    generateHtml();
  }, [convertTextToHtml, generateHtml, sourceText]);

  const findContainingSpan = useCallback(
    (
      htmlText: string,
      startPos: number,
      endPos: number
    ): { spanStart: number; spanEnd: number; className: string; contentStart: number; contentEnd: number } | null => {
      let spanStart = startPos;
      let className = '';
      let contentStart = startPos;

      while (spanStart >= 0) {
        if (htmlText[spanStart] === '<') {
          // Check if this is a span tag
          const tagMatch = htmlText.substring(spanStart).match(/^<span\s+class="([^"]+)"[^>]*>/);
          if (tagMatch) {
            // disabling because this is easier to read than destructuring
            // eslint-disable-next-line prefer-destructuring
            className = tagMatch[1];
            contentStart = spanStart + tagMatch[0].length;
            break;
          }
        }
        spanStart--;
      }

      // Look forward from endPos to find the closing span tag
      let spanEnd = endPos;
      let contentEnd = endPos;

      while (spanEnd < htmlText.length) {
        if (htmlText[spanEnd] === '<') {
          const closeMatch = htmlText.substring(spanEnd).match(/^<\/span>/);
          if (closeMatch) {
            contentEnd = spanEnd;
            spanEnd += closeMatch[0].length;
            break;
          }
        }
        spanEnd++;
      }

      if (spanStart >= 0 && spanEnd <= htmlText.length && className) {
        return {
          spanStart,
          spanEnd,
          className,
          contentStart,
          contentEnd,
        };
      }

      return null;
    },
    []
  );

  const highlightTextByCitations = useCallback(() => {
    if (!htmlText) {
      return;
    }
    const newParts: HTMLPart[] = [];
    const allTokenReferences = citations.map(cit => cit.tokenReferences).flat();

    // Build a mapping between plain text indices and HTML string indices
    // by stripping tags and tracking positions
    const plainTextToHtml: number[] = [];
    const htmlToPlainText: number[] = new Array(htmlText.length).fill(-1);
    let inTag = false;
    let plainTextIndex = 0;

    for (let i = 0; i < htmlText.length; i++) {
      if (htmlText[i] === '<') {
        inTag = true;
      }

      if (!inTag) {
        plainTextToHtml.push(i);
        htmlToPlainText[i] = plainTextIndex;
        plainTextIndex++;
      }

      if (htmlText[i] === '>') {
        inTag = false;
      }
    }

    const ranges = allTokenReferences
      .map(tokRef => {
        const startIndex = (tokRef as any).start_index ?? tokRef.startIndex;
        const endIndex = (tokRef as any).end_index ?? tokRef.endIndex;
        return {
          plainStart: startIndex,
          plainEnd: endIndex,
          htmlStart: plainTextToHtml[startIndex],
          htmlEnd: plainTextToHtml[endIndex - 1] + 1, // +1 to include last char
          text: tokRef.plain_text ?? tokRef.plainText,
          ref: tokRef,
        };
      })
      .sort((a, b) => a.htmlStart - b.htmlStart);

    let currentIndex = 0;

    ranges.forEach(range => {
      if (range.htmlStart === undefined || range.htmlEnd === undefined) {
        console.warn('Invalid range mapping:', range);
        return;
      }

      // Find the containing span information
      const spanInfo = findContainingSpan(htmlText, range.htmlStart, range.htmlEnd);

      if (spanInfo) {
        // Check if we're highlighting the entire span content or just part of it
        const isFullSpan = range.htmlStart <= spanInfo.contentStart && range.htmlEnd >= spanInfo.contentEnd;

        if (isFullSpan) {
          // Highlighting the entire span - use it as-is
          if (currentIndex < spanInfo.spanStart) {
            newParts.push({ text: htmlText.substring(currentIndex, spanInfo.spanStart) });
          }

          newParts.push({ text: htmlText.substring(spanInfo.spanStart, spanInfo.spanEnd), tokenReference: range.ref });
          currentIndex = spanInfo.spanEnd;
        } else {
          // Highlighting part of the span - need to split it
          if (currentIndex < spanInfo.spanStart) {
            newParts.push({ text: htmlText.substring(currentIndex, spanInfo.spanStart) });
          }

          // Extract the full content of the span
          const fullContent = htmlText.substring(spanInfo.contentStart, spanInfo.contentEnd);

          // Calculate positions relative to the content
          const highlightStartInContent = range.htmlStart - spanInfo.contentStart;
          const highlightEndInContent = range.htmlEnd - spanInfo.contentStart;

          // Split into: before + highlighted + after
          const beforeText = fullContent.substring(0, highlightStartInContent);
          const highlightedText = fullContent.substring(highlightStartInContent, highlightEndInContent);
          const afterText = fullContent.substring(highlightEndInContent);

          // Push the parts
          if (beforeText) {
            newParts.push({ text: `<span class="${spanInfo.className}">${beforeText}</span>` });
          }

          newParts.push({
            text: `<span class="${spanInfo.className}">${highlightedText}</span>`,
            tokenReference: range.ref,
          });

          if (afterText) {
            newParts.push({ text: `<span class="${spanInfo.className}">${afterText}</span>` });
          }

          currentIndex = spanInfo.spanEnd;
        }
      } else {
        // Fallback: if we can't find span info, use the original range
        if (currentIndex < range.htmlStart) {
          newParts.push({ text: htmlText.substring(currentIndex, range.htmlStart) });
        }

        const extractedText = htmlText.substring(range.htmlStart, range.htmlEnd);
        newParts.push({ text: extractedText, tokenReference: range.ref });

        currentIndex = range.htmlEnd;
      }
    });

    if (currentIndex < htmlText.length) {
      newParts.push({ text: htmlText.substring(currentIndex) });
    }

    setParts([...newParts]);
  }, [htmlText, citations, findContainingSpan]);

  useEffect(() => {
    highlightTextByCitations();
  }, [highlightTextByCitations]);

  // Memoize fallback parts to prevent re-renders when no citations
  const finalParts = useMemo(() => (parts.length > 0 ? parts : [{ text: htmlText }]), [parts, htmlText]);

  if (error) {
    return <Typography>{error}</Typography>;
  }

  if (!htmlText) {
    return null;
  }

  return <HighlightableTextWithCitation parts={finalParts} onAddCitation={onCitationPushed!} />;
};

export default HighlightedText;
